import { Checkbox, TableCell, TableRow, TableSortLabel, Box } from '@mui/material'
import { useReportsContext } from '../../context'

/**
 * Renders the header for the reports table.
 * Displays either dynamic columns based on user preferences or a default set of columns.
 *
 * @param {object} props - The component props.
 * @param {Array<object>} props.columnasParaMostrar - List of columns selected by the user to display.
 * @param {boolean} props.admin - Indicates if the current user has admin privileges.
 * @param {boolean} props.allChecked - State for the 'select all' checkbox.
 * @param {boolean} props.indeterminatedChecked - State for the 'select all' checkbox (indeterminate).
 * @param {function} props.setAllItemsSelected - Handler for the 'select all' checkbox change.
 * @param {string} props.typeReportsSelected - The currently selected report type.
 * @param {function} props.onColumnSelectorClick - Handler to open the column selector popover.
 * @param {number} props.widthTotal - The calculated total width of the table.
 */
const ReportTableHeader = ({
    columnasParaMostrar,
    admin,
    isOperator,
    isUniversalChief,
    isUniversalInspector,
    isOperatorInspector,
    allChecked,
    indeterminatedChecked,
    setAllItemsSelected,
    typeReportsSelected,
    onColumnSelectorClick,
    widthTotal,
    tipo,
    canSelect,
    isSearching
}) => {
    const { sortField, setSortField, sortOrder, setSortOrder } = useReportsContext()

    const headerStyle = { backgroundColor: '#f5f5f5', minWidth: 80, padding: '8px 8px', textAlign: 'center' }
    const actionHeaderStyle = { backgroundColor: '#f5f5f5', minWidth: 70, padding: '8px 8px', textAlign: 'center' }
    const checkboxHeaderStyle = { backgroundColor: '#f5f5f5', width: 60, padding: '0px 10px', textAlign: 'center' }

    const handleSort = (field) => {
        let fieldToUse = field
        if (field === 'idIndex') {
            fieldToUse = 'newIdIndex'
        }
        const isAsc = sortField === fieldToUse && sortOrder === 1
        setSortOrder(isAsc ? -1 : 1)
        setSortField(fieldToUse)
    }

    const SortableHeader = ({ field, children, sx = {} }) => (
        <TableCell sx={{ ...headerStyle, ...sx }} sortDirection={sortField === field ? (sortOrder === 1 ? 'asc' : 'desc') : false}>
            <TableSortLabel
                active={sortField === field}
                direction={sortField === field ? (sortOrder === 1 ? 'asc' : 'desc') : 'asc'}
                onClick={() => handleSort(field)}
            >
                <strong>{children}</strong>
            </TableSortLabel>
        </TableCell>
    )

    const renderDefaultHeader = () => (
        <>
            <TableCell sx={checkboxHeaderStyle}>
                {canSelect && (
                    <Checkbox
                        checked={allChecked}
                        indeterminate={indeterminatedChecked}
                        value={allChecked}
                        onChange={(e) => {
                            setAllItemsSelected(indeterminatedChecked ? false : e.target.checked)
                        }}
                    />
                )}
            </TableCell>
            <SortableHeader field="newIdIndex">N° OT</SortableHeader>
            <TableCell sx={headerStyle}><strong>Máquina</strong></TableCell>
            <TableCell sx={headerStyle}><strong>Horómetro Actual</strong></TableCell>
            <TableCell sx={headerStyle}><strong>Pauta</strong></TableCell>
            <TableCell sx={headerStyle}><strong>SPM</strong></TableCell>
            <TableCell sx={{ ...headerStyle, minWidth: 100 }}><strong>Flota</strong></TableCell>
            <TableCell sx={headerStyle}><strong>Progreso</strong></TableCell>
            <SortableHeader field="datePrev">Fecha <br /> Prevista</SortableHeader>

            {/* Ocultar Fechas de ejecucion para Asignar/Origen */}
            {(isSearching || ['En proceso', 'Por cerrar', 'Completadas', 'Ejecutando por operador', 'Reasignar', 'Revisión Supervisor', 'Revisión J. Maquinaria', 'Revisión SAP', 'Asignar', 'Origen', 'all', 'Todos'].includes(typeReportsSelected)) && (
                <>
                    <SortableHeader field="dateInit">Fecha <br /> Inicio</SortableHeader>
                    <SortableHeader field="endReport">Fecha <br /> Término</SortableHeader>
                </>
            )}

            <TableCell sx={headerStyle}><strong>Estado</strong></TableCell>

            {(isSearching || ['En proceso', 'Por cerrar', 'Completadas', 'Ejecutando por operador', 'Reasignar', 'Revisión Supervisor', 'Revisión J. Maquinaria', 'Revisión SAP', 'Asignar', 'Origen', 'all', 'Todos'].includes(typeReportsSelected)) && (
                <>
                    <TableCell sx={headerStyle}><strong>Hallazgos</strong></TableCell>
                    <TableCell sx={headerStyle}><strong>Sin Ejecutar</strong></TableCell>
                </>
            )}

            <TableCell sx={headerStyle}><strong>{(typeReportsSelected === 'Mantención' || tipo === 'Mantención') ? 'OMS SAP' : 'Aviso SAP'}</strong></TableCell>

            {/* Ocultar Asignado A para Asignar/Origen */}
            {(isSearching || (['En proceso', 'Por cerrar', 'Completadas', 'Ejecutando por operador', 'Reasignar', 'Revisión Supervisor', 'Revisión J. Maquinaria', 'Revisión SAP', 'all', 'Todos'].includes(typeReportsSelected) && typeReportsSelected !== 'Asignar' && typeReportsSelected !== 'Origen')) && (
                <TableCell sx={headerStyle}><strong>Asignado a</strong></TableCell>
            )}

            {(isSearching || (['En proceso', 'Por cerrar', 'Completadas', 'Ejecutando por operador', 'Reasignar', 'Revisión Supervisor', 'Revisión J. Maquinaria', 'Revisión SAP', 'all', 'Todos'].includes(typeReportsSelected) && typeReportsSelected !== 'Asignar' && typeReportsSelected !== 'Origen')) && (
                <TableCell sx={headerStyle}><strong>Asignado por</strong></TableCell>
            )}
            {(isSearching || typeReportsSelected === 'Por cerrar' || typeReportsSelected === 'Completadas') && (
                <TableCell sx={headerStyle}><strong>Jefe de turno</strong></TableCell>
            )}
            {(isSearching || typeReportsSelected === 'Por cerrar' || typeReportsSelected === 'Completadas') && (
                <TableCell sx={headerStyle}><strong>Jefe de Maquinaria</strong></TableCell>
            )}
            {(isSearching || typeReportsSelected === 'Completadas') && (
                <TableCell sx={headerStyle}><strong>Ejec. SAP</strong></TableCell>
            )}
            {!isOperator || isUniversalChief || isUniversalInspector || isOperatorInspector ? (
                <TableCell sx={headerStyle}><strong>Obra</strong></TableCell>
            ) : null}
            {(isSearching || typeReportsSelected === 'Completadas') && (
                <TableCell sx={headerStyle}><strong>Download</strong></TableCell>
            )}
            <TableCell sx={actionHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <strong>Acción</strong>
                </div>
            </TableCell>
        </>
    )

    const renderCustomHeader = () => (
        <>
            <TableCell sx={checkboxHeaderStyle}>
                {canSelect && (
                    <Checkbox
                        checked={allChecked}
                        indeterminate={indeterminatedChecked}
                        onChange={(e) => setAllItemsSelected(indeterminatedChecked ? false : e.target.checked)}
                    />
                )}
            </TableCell>
            {columnasParaMostrar.map((columna) => {
                const isSapId = columna.value === 'sapId'
                const displayName = isSapId
                    ? ((typeReportsSelected === 'Mantención' || tipo === 'Mantención') ? 'OMS SAP' : 'Aviso SAP')
                    : columna.name

                return (
                    <TableCell key={columna.id} sx={{ ...headerStyle, minWidth: columna.width || 80 }}>
                        <strong>{displayName}</strong>
                    </TableCell>
                )
            })}
            <TableCell sx={actionHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <strong>Acción</strong>
                </div>
            </TableCell>
        </>
    )

    return (
        <TableRow>
            {columnasParaMostrar && columnasParaMostrar.length > 0 ? renderCustomHeader() : renderDefaultHeader()}
        </TableRow>
    )
}

export default ReportTableHeader
